import {
  Atom01Icon,
  Bug01Icon,
  Database01Icon,
  Globe02Icon,
  PlayIcon,
  RadioIcon,
  Search01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import {
  findingsActionHitlConfig,
  findingToolHitlConfig,
  renderAssetsListCard,
  renderAssetsListResult,
  renderEndpointsCard,
  renderFindingHitlCard,
  renderFindingResult,
  renderFindingsCard,
  renderFindingsListResult,
  renderHarEntriesCard,
  renderMarkdownResult,
  renderSignalHitlCard,
  renderSignalResult,
  renderSignalsCard,
  renderSignalsListResult,
  signalHitlConfig,
  updateSignalHitlConfig,
} from './tools';
import { extractFindingMetadata, extractSignalMetadata } from './extractors';
import {
  renderFindingContent,
  renderFindingDescriptionContent,
  renderFindingEditContent,
  renderFindingEditModal,
  renderSignalContent,
  renderSignalEditContent,
  renderSignalEditModal,
  renderUpdateSignalContent,
  renderUpdateSignalEditContent,
  renderUpdateSignalEditModal,
} from './renderers';
import {
  renderEndpointResult,
  renderEndpointsListResult,
} from './tools/endpoints';
import {
  renderWebPageResult,
  renderWebPagesListResult,
} from './tools/web-pages';
import { renderHarEntriesResult } from './tools/har-entries';
import type { ToolConfig } from './tool-config-types';

export const buildIntelligenceToolConfigs = (): Record<string, ToolConfig> => ({
  'finding-tool': {
    name: 'Finding',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'finding',
    hitlConfig: findingToolHitlConfig,
    usesTabEdit: true,
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderEditModal: renderFindingEditModal,
    renderEditContent: renderFindingEditContent,
    renderContent: renderFindingContent,
    extractMetadata: extractFindingMetadata,
  },
  create_findings: {
    name: 'Log Findings',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'finding',
    hitlConfig: findingsActionHitlConfig,
    usesTabEdit: true,
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderEditModal: renderFindingEditModal,
    renderEditContent: renderFindingEditContent,
    renderContent: renderFindingContent,
    extractMetadata: extractFindingMetadata,
  },
  create_finding: {
    name: 'Log Finding',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    hitlConfig: findingsActionHitlConfig,
    usesTabEdit: true,
    contentType: 'finding',
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderEditModal: renderFindingEditModal,
    renderEditContent: renderFindingEditContent,
    renderContent: renderFindingContent,
    extractMetadata: extractFindingMetadata,
  },
  update_findings: {
    name: 'Update Findings',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'finding',
    hitlConfig: findingsActionHitlConfig,
    usesTabEdit: true,
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderEditModal: renderFindingEditModal,
    renderEditContent: renderFindingEditContent,
    renderContent: renderFindingContent,
    extractMetadata: extractFindingMetadata,
  },
  update_finding: {
    name: 'Update Finding',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'finding',
    hitlConfig: findingsActionHitlConfig,
    usesTabEdit: true,
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderEditModal: renderFindingEditModal,
    renderEditContent: renderFindingEditContent,
    renderContent: renderFindingContent,
    extractMetadata: extractFindingMetadata,
  },
  update_finding_description: {
    name: 'Revise Finding',
    icon: Atom01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'finding',
    hitlConfig: findingsActionHitlConfig,
    renderCard: renderFindingHitlCard,
    renderOutput: renderFindingResult,
    renderContent: renderFindingDescriptionContent,
    extractMetadata: extractFindingMetadata,
  },
  'signal-tool': {
    name: 'Signal',
    failedName: 'Signal creation failed',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: signalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderSignalEditModal,
    renderEditContent: renderSignalEditContent,
    renderContent: renderSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  'update-signal-tool': {
    name: 'Update Signal',
    icon: Target03Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: updateSignalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderUpdateSignalEditModal,
    renderEditContent: renderUpdateSignalEditContent,
    renderContent: renderUpdateSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  create_signal: {
    name: 'Raise Signal',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: signalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderSignalEditModal,
    renderEditContent: renderSignalEditContent,
    renderContent: renderSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  create_signals: {
    name: 'Raise Signals',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: signalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderSignalEditModal,
    renderEditContent: renderSignalEditContent,
    renderContent: renderSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  update_signal: {
    name: 'Update Signal',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: signalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderSignalEditModal,
    renderEditContent: renderSignalEditContent,
    renderContent: renderSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  update_signals: {
    name: 'Update Signals',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'signal',
    hitlConfig: signalHitlConfig,
    usesTabEdit: true,
    renderCard: renderSignalHitlCard,
    renderOutput: renderSignalResult,
    renderEditModal: renderSignalEditModal,
    renderEditContent: renderSignalEditContent,
    renderContent: renderSignalContent,
    extractMetadata: extractSignalMetadata,
  },
  'websearch-tool': {
    name: 'Web Search',
    icon: Search01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching the web...',
    renderOutput: renderMarkdownResult,
  },
  'search-tracked-endpoints': {
    name: 'Endpoints',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoints-list',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointsListResult,
  },
  'get-tracked-endpoint-schema': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'endpoint-details-tool': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'endpoint-details': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'endpoint-read': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'get-endpoint-details': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'read-endpoint-details': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'read-endpoint-details-tool': {
    name: 'Endpoint',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoint',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointResult,
  },
  'search-tracked-web-pages': {
    name: 'Web Pages',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'web-pages-list',
    runningText: 'Searching web pages...',
    renderOutput: renderWebPagesListResult,
  },
  'list-assets-tool': {
    name: 'Assets',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Listing assets...',
    renderCard: renderAssetsListCard,
    renderOutput: renderAssetsListResult,
  },
  'get-tracked-web-page': {
    name: 'Web Page',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'web-page',
    runningText: 'Reading web page...',
    renderOutput: renderWebPageResult,
  },
  'list-web-pages-for-endpoint': {
    name: 'Endpoint Webpages',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Finding related webpages...',
  },
  'list-endpoints-on-web-page': {
    name: 'Webpage Endpoints',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'endpoints-list',
    runningText: 'Finding related endpoints...',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointsListResult,
  },
  'web-page-details': {
    name: 'Web Page',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'web-page',
    runningText: 'Reading web page...',
    renderOutput: renderWebPageResult,
  },
  'web-page-read': {
    name: 'Web Page',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'web-page',
    runningText: 'Reading web page...',
    renderOutput: renderWebPageResult,
  },
  'read-web-page': {
    name: 'Web Page',
    icon: Globe02Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'web-page',
    runningText: 'Reading web page...',
    renderOutput: renderWebPageResult,
  },
  'har-entries-tool': {
    name: 'Network Log',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'har-entries',
    renderCard: renderHarEntriesCard,
    renderOutput: renderHarEntriesResult,
  },
  'signals-explorer-tool': {
    name: 'Search Signals',
    icon: Target03Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching signals...',
    renderCard: renderSignalsCard,
    renderOutput: renderSignalsListResult,
  },
  'findings-explorer-tool': {
    name: 'Search Findings',
    icon: Bug01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching findings...',
    renderCard: renderFindingsCard,
    renderOutput: renderFindingsListResult,
  },
  'endpoints-explorer-tool': {
    name: 'Search Endpoints',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching endpoints...',
    renderCard: renderEndpointsCard,
    renderOutput: renderEndpointsListResult,
  },
  'executions-explorer-tool': {
    name: 'Search Executions',
    icon: PlayIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching executions...',
  },
  'har-explorer-tool': {
    name: 'Search Network Log',
    icon: Database01Icon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Searching network log...',
    renderCard: renderHarEntriesCard,
  },
});
